import { Button, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { confirmDialog } from "@/utils/confirmDialog";
import { DATETIME_PICKER_FORMAT } from "@/config";
import { loadReceivePaymentSpecials } from "@/api/services/receivePaymentService";
import { useUserStore } from "@/stores/react/authStore";
import { dateTimeFormat } from "@/utils";
import AssignDialog, { type assignSubmitData } from "@/views/components/AssignDialog/AssignDialog";
import ConfirmPaymentDialog from "./components/payment/confirmPaymentDialog";
import EditPaymentDialog, { type ReceivePaymentUpdatePayload } from "./tradeOrder/components/dialog/editPaymentDialog";
import type {
    CancelReceivePaymentForm,
    ConfirmReceivePaymentForm,
    File as EntityFile,
    ReceivePayment,
    ReceivePaymentVo,
    ServiceSpecialVo
} from "@zhihaoscm/entity";
import { RECEIVE_PAYMENT_TYPE, PAYMENT_SUBMIT_TYPE } from "@zhihaoscm/meta";

type TradeCollectionVoucherFile = Pick<EntityFile, "id" | "name" | "path">;

export interface TradeCollectionDetailPageProps {
    loadDetail?: (id: number | string) => Promise<ReceivePaymentVo>;
    loadSpecials?: (keyword: string) => Promise<ServiceSpecialVo[]>;
    assignPayment?: (payload: { orderId: number; handlerId: string }) => Promise<unknown>;
    updatePayment?: (id: number | string, payload: Partial<ReceivePayment>) => Promise<unknown>;
    confirmPayment?: (payload: ConfirmReceivePaymentForm) => Promise<unknown>;
    uploadPaymentFile?: (file: Blob) => Promise<TradeCollectionVoucherFile>;
    previewVoucher?: (id: number | string, fileIds: number[]) => Promise<string | undefined>;
    cancelPayment?: (payload: CancelReceivePaymentForm) => Promise<unknown>;
    revokePayment?: (payload: CancelReceivePaymentForm) => Promise<unknown>;
    deletePayment?: (id: number | string) => Promise<unknown>;
    permissions?: string[];
    currentUserId?: number;
}

const PAYMENT_TYPE_LABELS: Record<number, string> = {
    1: RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.intro,
    2: RECEIVE_PAYMENT_TYPE.ADVANCE_PAYMENT.intro,
    3: RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.intro,
    4: RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.intro,
    5: RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.intro
};

const RECEIVE_STATE_LABELS: Record<number, string> = {
    1: "待支付",
    2: "已支付 (线上)",
    3: "未支付",
    4: "待确认支付",
    5: "已支付 (线下)"
};

/** 将金额统一为 Vue 详情页使用的两位小数格式。 */
function formatMoney(value: number | undefined): string {
    return (Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** 按旧 Vue DetailJson 的格式展示收款详情时间，隐藏 ISO 分隔符和秒数。 */
function formatCollectionDateTime(value: unknown): string {
    if (!value) return "-";
    return dateTimeFormat(String(value), DATETIME_PICKER_FORMAT);
}

/** 沿用 Vue BaseService.get 的收款详情接口路径。 */
export async function loadTradeCollectionDetail(id: number | string): Promise<ReceivePaymentVo> {
    const response = await request.service.get<ReceivePaymentVo>(`/admin/receive-payment/${id}`);
    return response.data;
}

/** 沿用 Vue 订单专员指派接口。 */
export async function assignTradeCollection(payload: { orderId: number; handlerId: string }): Promise<unknown> {
    const response = await request.service.put(`/admin/order/assign/${payload.orderId}/${payload.handlerId}`);
    return response.data;
}

/** 沿用 Vue 收款编辑接口，保留现有收款表单 payload。 */
export async function updateTradeCollection(id: number | string, payload: Partial<ReceivePayment>): Promise<unknown> {
    const response = await request.service.put(`/admin/receive-payment/${id}`, payload);
    return response.data;
}

/** 沿用 Vue 确认收款接口。 */
export async function confirmTradeCollection(payload: ConfirmReceivePaymentForm): Promise<unknown> {
    const response = await request.service.put("/admin/receive-payment/confirm", payload);
    return response.data;
}

/** 复用 Vue file-list 的上传接口，将付款凭证文件交给文件服务保存。 */
export async function uploadTradeCollectionVoucher(file: Blob): Promise<TradeCollectionVoucherFile> {
    const formData = new FormData();
    formData.append("file", file);
    // 延迟加载既有文件服务，避免详情页首屏引入服务装饰器链，同时保持 fileService.uploadFile 契约。
    const { fileService } = await import("@/api/services/fileService");
    return fileService.uploadFile(formData);
}

/** 查询 Vue 收款凭证文件服务并返回地址。 */
export async function previewTradeCollectionVoucher(
    _id: number | string,
    fileIds: number[]
): Promise<string | undefined> {
    const fileId = fileIds[0];
    if (!fileId) return undefined;
    const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${fileId}`);
    return response.data.path;
}

/** 沿用 Vue 取消收款接口。 */
export async function cancelTradeCollection(payload: CancelReceivePaymentForm): Promise<unknown> {
    const response = await request.service.put("/admin/receive-payment/cancel", payload);
    return response.data;
}

/** 沿用 Vue 撤回收款接口。 */
export async function revokeTradeCollection(payload: CancelReceivePaymentForm): Promise<unknown> {
    const response = await request.service.put("/admin/receive-payment/revoke", payload);
    return response.data;
}

/** 沿用 Vue 删除收款接口。 */
export async function deleteTradeCollection(id: number | string): Promise<unknown> {
    const response = await request.service.delete(`/admin/receive-payment/${id}`);
    return response.data;
}

/** 将采购企业信息映射为 Vue 收款详情中的采购账号文案。 */
function getCustomerLabel(detail: ReceivePaymentVo): string {
    const customer = detail.receivePayment.customerEnterprise;
    return `${customer?.name || customer?.realName || "-"} (${customer?.mobile || "-"})`;
}

/** 按 Vue 收款详情的商品类型保留商品详情跳转入口。 */
function getProductDetailPath(payment: ReceivePayment): string | undefined {
    const productId = payment.productId;
    if (!productId) return undefined;
    const paths: Record<number, string> = {
        1: "/productManagement/consignment/detail",
        2: "/productManagement/groupPurchase/detail",
        3: "/productManagement/bargain/detail"
    };
    const path = paths[Number(payment.productType)];
    return path ? `${path}?id=${productId}` : undefined;
}

/** 交易管理收款详情首屏，按 Vue 页面保留关联信息、账户信息和费用信息分组。 */
export function TradeCollectionDetailPage({
    loadDetail = loadTradeCollectionDetail,
    loadSpecials = loadReceivePaymentSpecials,
    assignPayment = assignTradeCollection,
    updatePayment = updateTradeCollection,
    confirmPayment = confirmTradeCollection,
    uploadPaymentFile = uploadTradeCollectionVoucher,
    previewVoucher = previewTradeCollectionVoucher,
    cancelPayment = cancelTradeCollection,
    revokePayment = revokeTradeCollection,
    deletePayment = deleteTradeCollection,
    permissions,
    currentUserId
}: TradeCollectionDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const storeUserId = useUserStore(state => state.userInfo.user?.id);
    const collectionId = new URLSearchParams(location.search).get("id") || "";
    const [detail, setDetail] = useState<ReceivePaymentVo | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignVisible, setAssignVisible] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [specialists, setSpecialists] = useState<ServiceSpecialVo[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number>();
    const [assignLoading, setAssignLoading] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);
    const [confirmVisible, setConfirmVisible] = useState(false);

    // 路由页面默认使用当前登录用户上下文；测试或嵌入场景传入 props 时保留显式覆盖能力。
    const effectivePermissions = permissions ?? storePermissions;
    const effectiveCurrentUserId = currentUserId ?? storeUserId;
    const hasPermission = (permission: string) => effectivePermissions.includes(permission);

    useEffect(() => {
        if (!collectionId) {
            setErrorMessage("缺少收款编号");
            return;
        }
        let disposed = false;
        void loadDetail(collectionId)
            .then(result => {
                if (!disposed) {
                    setDetail({
                        ...result,
                        receivePayment: { ...result.receivePayment, state: Number(result.receivePayment.state) }
                    });
                    setErrorMessage(null);
                }
            })
            .catch(() => {
                if (!disposed) setErrorMessage("收款详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [collectionId, loadDetail]);

    const payment = detail?.receivePayment;
    const showPaymentMethod = payment && ![1, 3].includes(Number(payment.state));
    const showPaymentInstruction = payment && [4, 5].includes(Number(payment.state));
    const latestConfirm = payment?.confirmOperateInfo?.[payment.confirmOperateInfo.length - 1];

    /** Vue 的当前专员判断用于限制修改和确认付款。 */
    const isHandler = Boolean(
        detail?.handlerId != null &&
        effectiveCurrentUserId != null &&
        String(detail.handlerId) === String(effectiveCurrentUserId)
    );
    const canManage = hasPermission("ROLE_RECEIVABLES_MANAGE");
    const canDeal = hasPermission("ROLE_RECEIVABLES_DEAL");
    const showEdit = payment && Number(payment.state) === 1 && isHandler && hasPermission("ROLE_TRADE_ORDER_DEAL");
    const showConfirm = payment && isHandler && (canDeal || canManage) && [1, 4].includes(Number(payment.state));
    /** 沿用 Vue 详情页权限契约，仅凭证查看角色可访问凭证入口。 */
    const showVoucher =
        payment &&
        hasPermission("ROLE_RECEIVABLES_R") &&
        ((payment.customFileId?.length && [3, 4].includes(Number(payment.state))) ||
            (Number(payment.state) === 5 && payment.paymentFileId?.length));
    const showCancel =
        payment &&
        canManage &&
        ((![1, 5].includes(Number(payment.paymentType)) && Number(payment.state) === 1) ||
            [4, 5].includes(Number(payment.state)));
    const showDelete =
        detail &&
        canManage &&
        Boolean(detail.orderDel || detail.orderState === 6) &&
        payment &&
        [3, 5].includes(Number(payment.state));

    /** 打开 Vue 通用指派弹框，并按订单接口加载可指派专员及当前专员回显。 */
    const openAssign = async () => {
        if (!payment) return;
        setSelectedHandlerId(detail?.handlerId == null ? undefined : Number(detail.handlerId));
        setSpecialists([]);
        setAssignError(null);
        setAssignVisible(true);
        setAssignLoading(true);
        try {
            setSpecialists((await loadSpecials("")) || []);
        } catch {
            setAssignError("订单专员查询失败");
        } finally {
            setAssignLoading(false);
        }
    };

    /** 支持 Vue 弹框中的姓名搜索，并保留查询失败反馈。 */
    const searchAssignSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            const result = (await loadSpecials(keyword)) || [];
            setSpecialists(result);
            setAssignError(null);
            return result;
        } catch {
            setSpecialists([]);
            setAssignError("订单专员查询失败");
            return [];
        } finally {
            setAssignLoading(false);
        }
    };

    /** 提交 Vue 通用指派弹框的 personId，保持订单指派接口参数契约。 */
    const submitAssign = async ({ officerId }: assignSubmitData) => {
        if (!payment || officerId == null) {
            setAssignError("请选择订单专员");
            throw new Error("请选择订单专员");
        }
        try {
            await assignPayment({ orderId: Number(payment.orderId), handlerId: String(officerId) });
            setAssignVisible(false);
        } catch {
            setAssignError("收款指派失败");
            setErrorMessage("收款指派失败");
            throw new Error("收款指派失败");
        }
    };

    /** 提交旧 Vue 编辑弹窗的完整业务字段。 */
    const editPayment = async (payload: ReceivePaymentUpdatePayload) => {
        if (!payment) return;
        try {
            await updatePayment(payment.id, payload);
            setEditVisible(false);
            const latest = await loadDetail(payment.id);
            setDetail(latest);
        } catch {
            setErrorMessage("收款修改失败");
        }
    };

    /** 打开确认付款前重新读取详情，确保提交最新凭证、说明和乐观锁版本号。 */
    const openConfirm = async () => {
        if (!payment) return;
        try {
            const latest = await loadDetail(payment.id);
            setDetail(latest);
            setConfirmVisible(true);
        } catch {
            setErrorMessage("收款详情加载失败");
        }
    };

    /** 提交共享确认付款弹窗，并在成功后刷新详情。 */
    const submitConfirm = async (payload: ConfirmReceivePaymentForm) => {
        try {
            await confirmPayment(payload);
            setConfirmVisible(false);
            setDetail(await loadDetail(payload.id));
        } catch {
            setErrorMessage("确认付款失败");
        }
    };

    /** 查看收款凭证。 */
    const viewVoucher = async () => {
        if (!payment) return;
        try {
            const path = await previewVoucher(payment.id, payment.paymentFileId || payment.customFileId || []);
            if (path) window.open(path, "_blank");
        } catch {
            setErrorMessage("收款凭证加载失败");
        }
    };

    /** 按 Vue 状态选择取消或撤回接口。 */
    const cancelOrRevoke = async () => {
        if (!payment) return;
        if (!(await confirmDialog(Number(payment.state) === 5 ? "是否撤回收款？" : "是否取消收款？"))) return;
        const payload = { id: payment.id, reason: "" };
        try {
            if (Number(payment.state) === 5) await revokePayment(payload);
            else await cancelPayment(payload);
        } catch {
            setErrorMessage("收款取消失败");
        }
    };

    /** 删除已取消订单关联的收款记录。 */
    const removePayment = async () => {
        if (!payment) return;
        if (!(await confirmDialog("是否删除收款记录？"))) return;
        try {
            await deletePayment(payment.id);
        } catch {
            setErrorMessage("收款删除失败");
        }
    };

    return (
        <section
            aria-label="trade-collection-detail-page"
            className="detail-json-page detail-page-transparent admin-detail-page"
        >
            <>
                {payment ? (
                    <div className="section-card admin-detail-header-card">
                        <ZhDetailHeader
                            leftInfos={[{ label: "收款编号", value: payment.id || "-" }]}
                            rightInfos={[{ label: "创建时间", value: formatCollectionDateTime(payment.createdTime) }]}
                            stateDesc={RECEIVE_STATE_LABELS[payment.state] || "-"}
                            title="收款详情"
                        />
                    </div>
                ) : null}
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {payment ? (
                    <div className="detail-json">
                        <section className="detail-json__block">
                            <div className="section-title">关联信息</div>
                            <ZhGrid className="admin-detail-grid" cols={4} gap={[12, 12]}>
                                <ZhBaseItem
                                    label="关联订单"
                                    value={
                                        payment.orderId ? (
                                            <Link to={`/tradeManage/tradeOrder/detail?id=${payment.orderId}`}>
                                                {payment.orderId}
                                            </Link>
                                        ) : (
                                            "-"
                                        )
                                    }
                                />
                                <ZhBaseItem
                                    label="关联商品"
                                    value={
                                        getProductDetailPath(payment) ? (
                                            <Link
                                                // 商品详情返回时保留当前收款详情上下文，和最新禅道验收要求一致。
                                                state={{
                                                    returnTo: `/tradeManage/collection/detail?id=${encodeURIComponent(
                                                        String(payment.id)
                                                    )}`
                                                }}
                                                to={getProductDetailPath(payment) as string}
                                            >
                                                {payment.productId}
                                            </Link>
                                        ) : (
                                            payment.productId || "-"
                                        )
                                    }
                                />
                                <ZhBaseItem
                                    label="关联提货"
                                    value={
                                        payment.deliveryId ? (
                                            <Link
                                                to={`/tradeManage/delivery/detail?id=${encodeURIComponent(String(payment.deliveryId))}`}
                                            >
                                                {payment.deliveryId}
                                            </Link>
                                        ) : (
                                            "-"
                                        )
                                    }
                                />
                                <ZhBaseItem
                                    label="采购账号"
                                    value={
                                        payment.customerId ? (
                                            <Link to={`/lianYunUser/userInfo?userId=${payment.customerId}`}>
                                                {getCustomerLabel(detail as ReceivePaymentVo)}
                                            </Link>
                                        ) : (
                                            getCustomerLabel(detail as ReceivePaymentVo)
                                        )
                                    }
                                />
                            </ZhGrid>
                        </section>
                        <section className="detail-json__block">
                            <div className="section-title">付款账户</div>
                            <ZhGrid className="admin-detail-grid" cols={4} gap={[12, 12]}>
                                <ZhBaseItem label="开户名称" value={payment.payBankInfo?.name || "-"} />
                                <ZhBaseItem label="银行账号" value={payment.payBankInfo?.account || "-"} />
                                <ZhBaseItem label="开户行" value={payment.payBankInfo?.bank || "-"} />
                            </ZhGrid>
                        </section>
                        <section className="detail-json__block">
                            <div className="section-title">费用信息</div>
                            <ZhGrid className="admin-detail-grid" cols={4} gap={[12, 12]}>
                                <ZhBaseItem label="费用类型" value={PAYMENT_TYPE_LABELS[payment.paymentType] || "-"} />
                                <ZhBaseItem label="支付金额" value={formatMoney(payment.amount)} />
                                {showPaymentMethod ? (
                                    <ZhBaseItem
                                        label="付款方式"
                                        value={payment.state === 2 ? "线上支付" : "线下支付"}
                                    />
                                ) : null}
                                {payment.state === 2 ? (
                                    <ZhBaseItem label="支付时间" value={formatCollectionDateTime(payment.payTime)} />
                                ) : null}
                                {showVoucher ? (
                                    <ZhBaseItem
                                        label="凭证提交类型"
                                        value={
                                            Number(payment.submitType) === PAYMENT_SUBMIT_TYPE.CUSTOMER.code
                                                ? "客户提交"
                                                : "平台提交"
                                        }
                                    />
                                ) : null}
                                {payment.state === 5 && latestConfirm ? (
                                    <>
                                        <ZhBaseItem label="确认人" value={latestConfirm.confirmUser || "-"} />
                                        <ZhBaseItem
                                            label="确认支付凭证时间"
                                            // 长标签沿用 Vue DetailJson 的单行展示，避免被详情字段右内边距截断换行。
                                            labelClassName="trade-collection-detail-confirm-time-label"
                                            value={formatCollectionDateTime(latestConfirm.confirmTime)}
                                        />
                                    </>
                                ) : null}
                                {showVoucher ? (
                                    <ZhBaseItem
                                        label="支付凭证"
                                        // Vue 将支付凭证作为整行字段展示，避免 React 三列布局将查看按钮挤到中间列。
                                        labelClassName="trade-collection-detail-voucher-label"
                                        span={3}
                                        value={
                                            <Button
                                                aria-label="查看凭证"
                                                onClick={() => void viewVoucher()}
                                                // Ant Button 链接默认带水平内边距，去除后与 Vue 查看凭证值的起点对齐。
                                                style={{ paddingInline: 0 }}
                                                type="link"
                                            >
                                                查看凭证
                                            </Button>
                                        }
                                    />
                                ) : null}
                                {showPaymentInstruction ? (
                                    <ZhBaseItem label="付款说明" span={3} value={payment.paymentInstruction || "--"} />
                                ) : null}
                            </ZhGrid>
                        </section>
                    </div>
                ) : null}
                <Space className="operation-fixed-btn" size={0}>
                    {payment ? (
                        <>
                            {hasPermission("ROLE_TRADE_ORDER_MANAGE") ? (
                                <Button
                                    aria-label={detail?.handlerId ? "变更指派" : "指派"}
                                    onClick={() => void openAssign()}
                                >
                                    {detail?.handlerId ? "变更指派" : "指派"}
                                </Button>
                            ) : null}
                            {showEdit ? (
                                <Button aria-label="修改" onClick={() => setEditVisible(true)}>
                                    修改
                                </Button>
                            ) : null}
                            {showConfirm ? (
                                <Button aria-label="确认付款" onClick={() => void openConfirm()}>
                                    确认付款
                                </Button>
                            ) : null}
                            {showCancel ? (
                                <Button
                                    aria-label={Number(payment.state) === 5 ? "撤回" : "取消"}
                                    onClick={() => void cancelOrRevoke()}
                                >
                                    {Number(payment.state) === 5 ? "撤回" : "取消"}
                                </Button>
                            ) : null}
                            {showDelete ? (
                                <Button aria-label="删除" onClick={() => void removePayment()}>
                                    删除
                                </Button>
                            ) : null}
                        </>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
                <AssignDialog
                    open={assignVisible}
                    title="指派专员"
                    info={detail || undefined}
                    officers={specialists}
                    searchSpecial={searchAssignSpecialists}
                    tips="跟进的订单数量"
                    countFiled="orderCount"
                    curOfficerId={selectedHandlerId}
                    confirmText="确定"
                    loading={assignLoading}
                    error={assignError}
                    onClose={() => {
                        setAssignVisible(false);
                        setAssignError(null);
                    }}
                    onCurOfficerIdChange={setSelectedHandlerId}
                    onSubmit={submitAssign}
                />
                <EditPaymentDialog
                    data={payment}
                    onCancel={() => setEditVisible(false)}
                    onSubmit={editPayment}
                    open={editVisible}
                />
                <ConfirmPaymentDialog
                    data={payment}
                    onCancel={() => setConfirmVisible(false)}
                    onSubmit={submitConfirm}
                    open={confirmVisible}
                    uploadFile={uploadPaymentFile}
                />
            </>
        </section>
    );
}

export default TradeCollectionDetailPage;
